import { Typography } from '@mui/material'
import { QuickTradeProto, QuoteType, SlTargetValueType } from '../../../../proto/generated/quick-trade'
import { useEffect, useMemo, useState } from 'react'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { Broker_OrderType, TransactionType } from '../../../../proto/generated/order'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import StrategyLegsViewer from '../../../strategyTemplate/StrategyLegsViewer'
import { hasConditionalRuleContent, summarizeConditionalEntry } from '../../../../util/conditionalEntry'
import {
    conditionalEntryStateFromTemplate,
    conditionalExitStateFromTemplate,
} from '../../../../util/quickTradeTemplateConditional'

type Props = {
    quickTrade: QuickTradeProto
    dontShowStrategyLegs?: boolean
}

function formatSlTargetSummary(params: QuickTradeProto['templateOrderExecutionParameters']): string {
    if (!params) return ''
    const isSingle = params.transactionType != null
    const parts: string[] = []
    if (params.addStopLoss && params.stopLoss != null) {
        parts.push(isSingle ? `SL: ${params.stopLoss} ${params.stopLossType === SlTargetValueType.PERCENTAGE ? '%' : 'pts'}` : `SL: ${params.stopLoss} MTM`)
    }
    if (params.addTarget && params.target != null) {
        parts.push(isSingle ? `TP: ${params.target} ${params.targetType === SlTargetValueType.PERCENTAGE ? '%' : 'pts'}` : `TP: ${params.target} MTM`)
    }
    return parts.length ? ` | ${parts.join(' | ')}` : ''
}

function formatConditionalSummary(
    params: QuickTradeProto['templateOrderExecutionParameters'],
    displaySymbol: string
): string {
    if (!params) return ''
    const parts: string[] = []
    const entry = conditionalEntryStateFromTemplate(params)
    const exit = conditionalExitStateFromTemplate(params)
    if (entry.enabled && hasConditionalRuleContent(entry.condition)) {
        const summary = summarizeConditionalEntry(entry.condition, displaySymbol)
        if (summary) parts.push(`Cond. Entry: ${summary}`)
    }
    if (exit.enabled && hasConditionalRuleContent(exit.condition)) {
        const summary = summarizeConditionalEntry(exit.condition, displaySymbol)
        if (summary) parts.push(`Cond. Exit: ${summary}`)
    }
    return parts.length ? ` | ${parts.join(' | ')}` : ''
}

const QuickTradeTooltip = ({ quickTrade, dontShowStrategyLegs = false }: Props) => {
    const { primary } = useSelector(selectPrimarySecurity)
    const computedSecurity = useMemo(() => {
        return quickTrade?.security?.code ? quickTrade?.security : primary
    }, [primary, quickTrade])
    const secKey = useMemo(() => getSecurityKey(computedSecurity?.exchange ?? 0, computedSecurity?.code ?? ''), [computedSecurity])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => {
        if (secKey) getSecurityAsync(secKey).then(setSecurity)
    }, [secKey])

    const transactionType = useMemo(() => {
        return quickTrade.templateOrderExecutionParameters.transactionType ? quickTrade.templateOrderExecutionParameters.transactionType : undefined
    }, [quickTrade.templateOrderExecutionParameters.transactionType])

    const quantityRender = useMemo(() => {
        const params = quickTrade?.templateOrderExecutionParameters
        const isSingleLeg = params?.transactionType != null
        if (params?.amount != null) {
            return `Amt: ${params.amount}`
        } else if (params?.lots != null) {
            return `${params.lots} ${params.lots > 1 ? 'Lots' : 'Lot'}`
        } else if (params?.quantity != null) {
            return `${params.quantity}`
        } else if (!isSingleLeg && params?.multiplier != null) {
            return `${params.multiplier} Multiplier`
        }
        return ''
    }, [quickTrade])

    const orderTypeRender = useMemo(() => {
        const params = quickTrade?.templateOrderExecutionParameters
        const brokerOrderType = params?.brokerOrderType
        if (brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER) {
            return 'Market'
        }
        if (brokerOrderType === Broker_OrderType.LIMIT_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_ORDER) {
            let limitData = ''
            switch (params?.quoteType) {
                case QuoteType.BIPS: {
                    let sign = (params.quoteValue ?? 0) > 0 ? 1 : -1
                    if (params?.transactionType === TransactionType.BUY) {
                        sign = -1 * sign
                    }
                    limitData = `${sign > 0 ? '+' : '-'}${Math.abs(params.quoteValue ?? 0)} BIP${[0, 1].includes(Math.abs(params.quoteValue ?? 0)) ? '' : 'S'}`
                    break
                }
                case QuoteType.TICKS:
                    limitData = `${(params.quoteValue ?? 0) > 0 ? '+' : ''}${params.quoteValue ?? 0} TICK${[0, 1].includes(Math.abs(params.quoteValue ?? 0)) ? '' : 'S'}`
                    break
                case QuoteType.LEVEL:
                    limitData = `${(params.quoteValue ?? 0) > 0 ? '+' : ''}${params.quoteValue ?? 0} LEVEL${[0, 1].includes(Math.abs(params.quoteValue ?? 0)) ? '' : 'S'}`
                    break
            }
            return limitData ? `Limit: ${limitData}` : 'Limit'
        }
        return ''
    }, [quickTrade])

    const slTargetSummary = useMemo(() => formatSlTargetSummary(quickTrade.templateOrderExecutionParameters), [quickTrade.templateOrderExecutionParameters])
    const conditionalSummary = useMemo(
        () => formatConditionalSummary(quickTrade.templateOrderExecutionParameters, security?.symbol ?? ''),
        [quickTrade.templateOrderExecutionParameters, security?.symbol]
    )

    return quickTrade.templateOrderExecutionParameters.transactionType ? (
        <div className="font-feature-num" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Typography
                component={'span'}
                className={`
                        ${transactionType === TransactionType.BUY ? 'buy-button' : 'sell-button'}
                    `}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '5px' }}
            >
                {transactionType === TransactionType.BUY ? 'B' : 'S'}
            </Typography>
            <Typography component={'span'} variant="caption" sx={{ color: (theme) => theme.palette.text.primary, textWrap: 'nowrap' }}>
                {security?.symbol} × {quantityRender} @{orderTypeRender}
                {slTargetSummary}
                {conditionalSummary}
            </Typography>
        </div>
    ) : (
        <div className="font-feature-num" style={{ minWidth: dontShowStrategyLegs ? 0 : '400px' }}>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.primary, lineHeight: '20px' }}>
                {security?.symbol} @{orderTypeRender}{' '}
                {quickTrade?.templateOrderExecutionParameters?.multiplier
                    ? ` | Multiplier: ${quickTrade?.templateOrderExecutionParameters?.multiplier}`
                    : `| Amount: ${quickTrade?.templateOrderExecutionParameters?.amount}`}
                {slTargetSummary}
                {conditionalSummary}
            </Typography>
            {dontShowStrategyLegs ? null : quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs ? (
                <StrategyLegsViewer strategyLegs={quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs} />
            ) : null}
        </div>
    )
}

export default QuickTradeTooltip
